import { CodeBook, ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { api } from "@king-ict/eupisi-ui/utils";

import { SchoolCandidate } from "../../models";
import { Committee } from "../../models/Committee/Committee";
import { Questionnaire } from "../../models/Questionnaire";
import { RequestMetadata, RequestMetadataSearch, Request,
    ParentChild, RequestType, RequestStepType, 
    ParentChildDetails, PersonDetails, DocumentType, 
    CreateRequestSchoolCandidate, CommitteeDecision, 
    DecisionProposal, Form7, RequestStep,
    EnrollmentDecision, 
    OutsideEnrollmentAreaStatus,
    RequestDataSearch,
    RequestData,
    RequestList,
    RegistrationForm,
    LanguageCourseDecision,
    RequestTypeChange} from "../../models/Request";
import { MedicalExamination } from "../../models/Request/MedicalExamination";
import { PersonType } from "../../models/Request/PersonType";
import { CodeBookPagedFilter, HierarchicalCodeBook, 
    HierarchicalCodeBookPagedFilter, SaveResult, 
    OibServiceResult, 
    PersonIdentification} from "../../models/Shared";
import { OtherParentValidationResult } from "../../models/Shared/OtherParentValidationResult";

const apiUrlPrefix: string = "request";

export const searchRequestMetadata = async (search: RequestMetadataSearch) => {
    return api.post(
        `${apiUrlPrefix}/search`,
        search
    ) as Promise<PagedResult<RequestMetadata>>
}

export const searchRequestData = async (search: RequestDataSearch) => {
    return api.post(
        `${apiUrlPrefix}/search-data`,
        search
    ) as Promise<PagedResult<RequestData>>
}

export const getParentChildServiceData = async (data: ParentChild) => {
    return api.post(
        `${apiUrlPrefix}/service-data`,
        data
    ) as Promise<OibServiceResult<ParentChildDetails>>
}

export const getPersonServiceData = async (data: PersonIdentification) => {
    return api.post(
        `${apiUrlPrefix}/service-data-single`,
        data
    ) as Promise<OibServiceResult<PersonDetails>>
}

/**
 * Creates the OtherParent details on the Request from the service data using
 * the otherParentPerson information.
 * This can only be called from the OtherParentData step/substep.
 * @param requestId The Request.id
 * @param otherParentPerson OIB, FirstName, and LastName of the OtherParent.
 * @returns The newly updated Request as a SaveResult.
 */
 export const createOtherParentServiceData = async (requestId: number, otherParentPersonDetails: PersonDetails) => {
    return api.post(
      `${apiUrlPrefix}/create-other-parent/${requestId}`,
      otherParentPersonDetails.person
    ) as Promise<OtherParentValidationResult<Request>>;
  };
  
export const updateServiceData = async (id: number, personType: PersonType) => {
    return api.get(
        `${apiUrlPrefix}/update-service-data/${id}/${personType}`
    ) as Promise<SaveResult<Request>>
};

export const createRequest = async (data: ParentChildDetails, type: RequestType, institutionId: number, force?: boolean) => {
    return api.post(
        (force ?? false) ? `${apiUrlPrefix}/create/${type}/${institutionId}/${force}` : `${apiUrlPrefix}/create/${type}/${institutionId}`,
        data
    ) as Promise<SaveResult<Request>>
}

export const getRequest = async (id: number) => {
    return api.get(
        `${apiUrlPrefix}/get/${id}`
    ) as Promise<Request>
}

export const deleteRequest = async (id: number) => {
    return api.delete(
        `${apiUrlPrefix}/delete/${id}`
    ) as Promise<SaveResult<boolean>>
}

export const saveRequest = async (item: Request, stepType?: RequestStepType) => {
    return api.post(
        stepType ? `${apiUrlPrefix}/save/${stepType}` : `${apiUrlPrefix}/save`,
        item
    ) as Promise<SaveResult<Request>>
}

export const setRequestStep = async (item: Request, step: RequestStep) => {
    return api.post(
        `${apiUrlPrefix}/set-step/${step}`,
        item
    ) as Promise<SaveResult<Request>>
}

export const changeRequestType = async (item: RequestTypeChange) => {
    return api.post(
        `${apiUrlPrefix}/set-type`,
        item
    ) as Promise<SaveResult<Request>>
}

export const approveRequest = async (id: number) => {
    return api.post(
        `${apiUrlPrefix}/approve/${id}`
    ) as Promise<SaveResult<Request>>
}

export const cancelRequest = async (id: number) => {
    return api.post(
        `${apiUrlPrefix}/cancel/${id}`
    ) as Promise<SaveResult<Request>>
}

export const revertRequest = async (id: number) => {
    return api.post(
        `${apiUrlPrefix}/revert/${id}`
    ) as Promise<SaveResult<Request>>
}

export const confirmRequest = async (item: RequestList) => {
    return api.post(
        `${apiUrlPrefix}/confirm`,
        item
    ) as Promise<SaveResult<RequestList>>
}

export const generateRequest = async (item: RequestList) => {
    return api.post(
        `${apiUrlPrefix}/generate`,
        item
    ) as Promise<SaveResult<RequestList>>
}

export const setOutsideEnrollmentAreaStatus = async (id: number, status: OutsideEnrollmentAreaStatus, isApproved: boolean) => {
    return api.post(
        `${apiUrlPrefix}/set-outside-enrollment-status/${id}/${status}/${isApproved}`
    ) as Promise<SaveResult<Request>>
}

export const getRequestExport = async (search: RequestMetadataSearch, type: ExportTypeEnum) => {
    return api.download(
        `${apiUrlPrefix}/export/${type}`,
        "post",
        search
    ) as Promise<any>
}

export const getRequestDataExport = async (search: RequestDataSearch, type: ExportTypeEnum) => {
    return api.download(
        `${apiUrlPrefix}/export-data/${type}`,
        "post",
        search
    ) as Promise<any>
}

export const getRequestDocument = async (id: number, requestId: number, type: DocumentType) => {
    return api.download(
        `${apiUrlPrefix}/document/${id}/${requestId}/${type}`
    ) as Promise<any>
}

export const createSchoolCandidate = async (create: CreateRequestSchoolCandidate) => {
    return api.post(
        `${apiUrlPrefix}/school-candidate/`,
        create
    ) as Promise<SaveResult<SchoolCandidate>>
};

// Committee
export const getCommittee = async (requestId: number, committeeId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-committee/${requestId}/${committeeId}`
    ) as Promise<Committee>
}

// CommitteeDecision
export const getCommitteeDecisionList = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-committee-decision-list/${requestId}`
    ) as Promise<CommitteeDecision[]>
}

export const getCommitteeDecision = async (committeeDecisionId: number, requestId: number, committeeEmployeeId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-committee-decision/${committeeDecisionId}/${requestId}/${committeeEmployeeId}`
    ) as Promise<CommitteeDecision>
}

export const saveCommitteeDecision = async (item: CommitteeDecision) => {
    return api.post(
        `${apiUrlPrefix}/save-committee-decision/`,
        item
    ) as Promise<SaveResult<CommitteeDecision>>
}

export const getDifficultyTypeList = async () => {
    return api.get(
        `${apiUrlPrefix}/get-difficulty-types`
    ) as Promise<CodeBook[]>
}

// DecisionProposal
export const getDecisionProposal = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-decision-proposal/${requestId}`
    ) as Promise<DecisionProposal>
}

export const saveDecisionProposal = async (item: DecisionProposal) => {
    return api.post(
        `${apiUrlPrefix}/save-decision-proposal/`,
        item
    ) as Promise<SaveResult<DecisionProposal>>
}

// Form7
export const getForm7 = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-form7/${requestId}`
    ) as Promise<Form7>
}

export const saveForm7 = async (item: Form7) => {
    return api.post(
        `${apiUrlPrefix}/save-form7/`,
        item
    ) as Promise<SaveResult<Form7>>
}

// EnrollmentDecision
export const getEnrollmentDecision = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-enrollment-decision/${requestId}`
    ) as Promise<EnrollmentDecision>
}

export const saveEnrollmentDecision = async (item: EnrollmentDecision) => {
    return api.post(
        `${apiUrlPrefix}/save-enrollment-decision/`,
        item
    ) as Promise<SaveResult<EnrollmentDecision>>
}

// RegistrationForm
export const getRegistrationForm = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-registration-form/${requestId}`
    ) as Promise<RegistrationForm>
}

export const saveRegistrationForm = async (item: RegistrationForm) => {
    return api.post(
        `${apiUrlPrefix}/save-registration-form/`,
        item
    ) as Promise<SaveResult<RegistrationForm>>
}

// LanguageCourseDecision
export const getLanguageCourseDecision = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-language-decision/${requestId}`
    ) as Promise<LanguageCourseDecision>
}

export const saveLanguageCourseDecision = async (item: LanguageCourseDecision) => {
    return api.post(
        `${apiUrlPrefix}/save-language-decision/`,
        item
    ) as Promise<SaveResult<LanguageCourseDecision>>
}

// MedicalExamination
export const getMedicalExamination = async (requestId: number) => {
    return api.get(
        `${apiUrlPrefix}/get-medical-examination/${requestId}`
    ) as Promise<MedicalExamination>
}

export const saveMedicalExamination = async (item: MedicalExamination) => {
    return api.post(
        `${apiUrlPrefix}/save-medical-examination/`,
        item
    ) as Promise<SaveResult<MedicalExamination>>
}

// Questionnaire
export const getInitialQuestionnaire = async () => {
    return api.get(
        `${apiUrlPrefix}/get-questionnaire`
    ) as Promise<Questionnaire>
}

export const getInitialQuestionnaireExport = async () => {
    return api.download(
        `${apiUrlPrefix}/export`
    ) as Promise<any>
}

export const getQuestionnaireExport = async (id: number) => {
    return api.download(
        `${apiUrlPrefix}/export/${id}`
    ) as Promise<any>
}

